<template>
    <div class="account-voicemails">
        <v-progress-linear
            v-if="loading"
            :indeterminate="true"
            color="success"
        ></v-progress-linear>
        <div v-if="!loading && !voicemails.length" class="account-comm-empty">
            No voicemails
        </div>
        <div
            v-for="(voicemail, index) in voicemails"
            :key="voicemail.id || index"
            class="account-comm-item"
            :class="{ 'account-comm-item--open': voicemail.expanded }"
        >
            <div class="account-comm-item-header" @click="toggle(voicemail)">
                <div class="account-comm-item-avatar account-comm-item-avatar--voicemail">
                    <v-icon small dark>mdi-voicemail</v-icon>
                </div>
                <div class="account-comm-item-summary">
                    <div class="account-comm-item-title-row">
                        <span class="account-comm-item-title">
                            {{ voicemail.phoneNumber || voicemail.from || "Unknown caller" }}
                        </span>
                    </div>
                    <div class="account-comm-item-preview">
                        {{ preview(voicemail) }}
                    </div>
                </div>
                <div class="account-comm-item-meta">
                    <div class="account-comm-item-date">{{ voicemail.date | time }}</div>
                    <v-chip
                        v-if="voicemail.status === 'RECEIVED'"
                        small
                        text-color="white"
                        color="info"
                        class="account-comm-chip"
                    >
                        Received
                    </v-chip>
                    <v-chip
                        v-else-if="voicemail.status === 'COMPLETED'"
                        small
                        text-color="white"
                        color="success"
                        class="account-comm-chip"
                    >
                        Completed
                    </v-chip>
                </div>
                <v-icon class="account-comm-item-chevron">
                    {{ voicemail.expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                </v-icon>
            </div>

            <div v-if="voicemail.expanded" class="account-comm-item-body">
                <div
                    class="account-comm-actions account-comm-actions--top"
                    v-if="voicemail.communicationID && voicemail.communicationStatus === 'PENDING'"
                >
                    <v-btn
                        color="success"
                        outline
                        small
                        :loading="completeProcessing && completeTarget === voicemail"
                        @click="openCompleteDialog(voicemail)"
                    >
                        <v-icon small class="mr-1">mdi-check</v-icon>
                        Mark done
                    </v-btn>
                </div>

                <div v-if="voicemail.note" class="account-comm-note mb-2">
                    Note: {{ voicemail.note }}
                </div>

                <voicemail-preview
                    v-if="voicemail.id"
                    :key="voicemail.id"
                    :row="voicemail"
                    :hideAssign="true"
                ></voicemail-preview>
            </div>
        </div>
        <div v-if="hasMore" class="account-comm-load-more">
            <v-btn
                small
                outline
                color="success"
                :loading="loadingMore"
                @click="loadMore"
            >
                Load more voicemails
            </v-btn>
        </div>

        <v-dialog v-model="completeDialog" persistent max-width="480">
            <v-card>
                <v-card-title class="account-comm-complete-header">
                    <v-icon color="white" class="mr-2">mdi-check-circle</v-icon>
                    Mark done
                </v-card-title>
                <v-card-text class="pt-4">
                    <v-textarea
                        v-model="completeNote"
                        label="Note (required)"
                        hint="A note is required to mark a communication as done."
                        persistent-hint
                        outline
                        rows="3"
                        autofocus
                    ></v-textarea>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="default" dark @click="cancelCompleteDialog">
                        Cancel
                    </v-btn>
                    <v-btn
                        color="success"
                        :disabled="!completeNote || !completeNote.trim()"
                        :loading="completeProcessing"
                        @click="confirmComplete"
                    >
                        Confirm
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>
<script>
const PAGE_SIZE = 25;

export default {
    data() {
        return {
            loading: true,
            loadingMore: false,
            hasMore: false,
            voicemails: [],
            totalCount: 0,
            pendingCount: 0,
            pendingFromApi: false,
            user: "",
            completeDialog: false,
            completeNote: "",
            completeTarget: null,
            completeProcessing: false,
        };
    },
    async created() {
        this.user = this.$store.state.authentication.user;
        await this.loadCount();
        await this.loadVoicemails();
    },
    methods: {
        preview(voicemail) {
            var text = (voicemail.transcriptionSummary || voicemail.transcription || voicemail.subject || "")
                .replace(/<[^>]+>/g, " ")
                .replace(/\s+/g, " ")
                .trim();
            if (text.length > 90) {
                return text.substring(0, 90) + "...";
            }
            return text || "Voicemail recording";
        },
        applyCount(response) {
            if (response && typeof response === "object") {
                this.totalCount = response.total || 0;
                this.pendingCount = response.pending || 0;
                this.pendingFromApi = true;
            } else {
                this.totalCount = response || 0;
                this.pendingFromApi = false;
            }
        },
        async loadCount() {
            try {
                this.applyCount(
                    await this.$http.get(
                        "api/voicemails/getCountForAccount?accountID=" +
                            this.$route.params.id
                    )
                );
            } catch (e) {
                this.totalCount = 0;
                this.pendingCount = 0;
            }

            this.$emit("loaded", this.totalCount, this.pendingCount);
        },
        async loadVoicemails() {
            this.loading = true;

            try {
                const voicemails = await this.$http.get(
                    "api/voicemails/getForAccount?accountID=" +
                        this.$route.params.id +
                        "&skip=0&top=" +
                        PAGE_SIZE
                );
                this.voicemails = voicemails || [];
                this.hasMore = (voicemails || []).length === PAGE_SIZE;
            } catch (e) {
                this.voicemails = [];
                this.hasMore = false;
            }

            if (!this.pendingFromApi) {
                this.pendingCount = (this.voicemails || []).filter(function(voicemail) {
                    return voicemail.communicationStatus === "PENDING";
                }).length;
            }

            this.$emit("loaded", this.totalCount, this.pendingCount);
            this.loading = false;
        },
        async loadMore() {
            this.loadingMore = true;

            try {
                const voicemails = await this.$http.get(
                    "api/voicemails/getForAccount?accountID=" +
                        this.$route.params.id +
                        "&skip=" +
                        this.voicemails.length +
                        "&top=" +
                        PAGE_SIZE
                );
                const newItems = voicemails || [];
                this.voicemails = this.voicemails.concat(newItems);
                this.hasMore = newItems.length === PAGE_SIZE;
            } catch (e) {
                this.hasMore = false;
            }

            this.loadingMore = false;
        },
        toggle(voicemail) {
            this.$set(voicemail, "expanded", !voicemail.expanded);
        },
        openCompleteDialog(voicemail) {
            this.completeTarget = voicemail;
            this.completeNote = "";
            this.completeDialog = true;
        },
        cancelCompleteDialog() {
            this.completeDialog = false;
            this.completeTarget = null;
            this.completeNote = "";
        },
        async confirmComplete() {
            if (!this.completeNote || !this.completeNote.trim() || !this.completeTarget) {
                this.$notify({
                    group: "notification",
                    text: "You can't mark as completed without entering a note.",
                    type: "error",
                });
                return;
            }

            var voicemail = this.completeTarget;
            this.completeProcessing = true;

            try {
                await this.$http.post("api/communications/updateStatus", {
                    communicationID: voicemail.communicationID,
                    status: "COMPLETED",
                    note: this.completeNote,
                    user: this.user,
                });
                this.$notify({
                    group: "notification",
                    text: "Voicemail marked done",
                    type: "success",
                });
                this.completeDialog = false;
                this.completeTarget = null;
                this.completeNote = "";
                await this.loadCount();
                await this.loadVoicemails();
            } catch (e) {
                this.$notify({
                    group: "notification",
                    text: "Could not update voicemail",
                    type: "error",
                });
            }

            this.completeProcessing = false;
        },
    },
};
</script>
<style>
.account-voicemails {
    max-height: 600px;
    overflow-y: auto;
    padding: 6px;
}

.account-voicemails .account-comm-actions {
    margin-top: 0;
    margin-bottom: 10px;
}

.account-voicemails .account-comm-actions .v-btn {
    margin-left: 0;
    margin-right: 8px;
}

.account-comm-item-avatar--voicemail {
    background: #5e35b1;
}

.account-comm-note {
    font-size: 13px;
    color: #546e7a;
}
</style>
